// Client for the R2-backed file store (/api/file). Two uses: the owner attaches
// the executed/signed contract PDF, and proof of payment (a bank receipt or a
// signed acknowledgement) is attached to a payment logged against a certificate.
// Ids are unguessable, so a stored id is safe to carry in a shared document.

// StoredFile is declared with the contract model (it is embedded in the synced
// brief and the ledger blob, so only the reference travels, never the bytes).
import type { StoredFile } from "../contracts/types";
export type { StoredFile };

export const FILE_MAX = 15 * 1024 * 1024; // must match FILE_MAX in server/index.ts
export const FILE_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/webp"];
export const FILE_ACCEPT = FILE_TYPES.join(",");

/** Where to fetch a stored file. Same-origin, so a plain link opens it inline. */
export function fileUrl(id: string): string {
  return `/api/file/${id}`;
}

export function fileSize(bytes: number): string {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Upload a file. Validates client-side first so the user gets a useful message
 * instead of a 413/415 round-trip. The body is the raw bytes — the server reads
 * the type from content-type and the display name from x-file-name. */
export async function uploadFile(file: File): Promise<StoredFile> {
  if (!FILE_TYPES.includes(file.type)) throw new Error("Only PDF, PNG, JPEG or WebP may be uploaded.");
  if (!file.size) throw new Error("That file is empty.");
  if (file.size > FILE_MAX) throw new Error(`That file is ${fileSize(file.size)} — the limit is ${FILE_MAX / 1024 / 1024} MB.`);
  const r = await fetch("/api/file", {
    method: "POST",
    // Headers must be latin-1, so the name is percent-encoded here and decoded
    // server-side — otherwise an em dash (which these project names are full of)
    // either throws on fetch or arrives mangled.
    headers: { "Content-Type": file.type, "x-file-name": encodeURIComponent(file.name.slice(0, 200)) },
    body: file,
  });
  if (!r.ok) throw new Error((await r.text()) || `Upload failed (HTTP ${r.status}).`);
  const out = (await r.json()) as { id: string; name?: string; size?: number };
  return { id: out.id, name: out.name || file.name, size: out.size ?? file.size, uploadedAt: new Date().toISOString() };
}

/** Delete a stored file. Owner only — a 401 means the session lapsed. */
export async function deleteFile(id: string): Promise<void> {
  const r = await fetch(`/api/file/${id}`, { method: "DELETE" });
  if (r.status === 401) throw new Error("Sign in to remove an uploaded file.");
  if (!r.ok) throw new Error((await r.text()) || `Could not remove the file (HTTP ${r.status}).`);
}
